import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { getCases, caseService } from "../../services/api"
import { Case } from "../../models/Case.model"

// Função para garantir que os dados do caso são serializáveis
const ensureCaseSerializable = (caseData) => {
  if (!caseData) return caseData

  // Converter strings de preço para números
  if (caseData.services && Array.isArray(caseData.services)) {
    caseData.services = caseData.services.map((service) => ({
      ...service,
      servicePrice: parseFloat(service.servicePrice || 0),
      productPrice: parseFloat(service.productPrice || 0),
      additionalPrice: parseFloat(service.additionalPrice || 0),
      totalPrice: parseFloat(service.totalPrice || 0)
    }))
  }

  if (caseData.totalPrice) {
    caseData.totalPrice = parseFloat(caseData.totalPrice)
  }

  if (caseData.shippingPrice) {
    caseData.shippingPrice = parseFloat(caseData.shippingPrice)
  }

  if (caseData.totalWithShipping) {
    caseData.totalWithShipping = parseFloat(caseData.totalWithShipping)
  }

  return caseData
}

// Initial state
const initialState = {
  cases: {},
  caseIds: [], // Array to maintain server order
  archivedCases: {},
  currentCase: null,
  loading: false,
  error: null,
  filters: {
    status: "all",
    search: "",
    dateRange: null,
    owner: null,
    patient: null
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15
  },
  archivedPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15
  },
  statistics: {
    totalCases: 0,
    byStatus: {},
    byOwner: {},
    avgCompletionTime: 0
  }
}

// Thunk para buscar casos
export const fetchCases = createAsyncThunk(
  "cases/fetchCases",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await getCases(filters)

      // Verificar diferentes estruturas de resposta possíveis
      let cases = []
      let pagination = {}
      let statistics = {}

      if (response) {
        // Estrutura 1: response.obj.cases
        if (
          response.obj &&
          response.obj.cases &&
          Array.isArray(response.obj.cases)
        ) {
          cases = response.obj.cases
          pagination = response.obj.pagination || {}
          statistics = response.obj.statistics || {}
        }
        // Estrutura 2: response.cases
        else if (response.cases && Array.isArray(response.cases)) {
          cases = response.cases
          pagination = response.pagination || {}
          statistics = response.statistics || {}
        }
        // Estrutura 3: response é um array direto
        else if (Array.isArray(response)) {
          cases = response
          pagination = {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.length
          }
          statistics = { totalCases: response.length }
        }
        // Estrutura 4: response.data.cases
        else if (response.data && response.data.cases) {
          cases = response.data.cases
          pagination = response.data.pagination || {}
          statistics = response.data.statistics || {}
        }
        // Estrutura 5: response.data é um array
        else if (response.data && Array.isArray(response.data)) {
          cases = response.data
          pagination = {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.data.length
          }
          statistics = { totalCases: response.data.length }
        }
      }

      // Garantir que todos os casos são serializáveis
      const serializableCases = cases.map((caseItem) =>
        ensureCaseSerializable(caseItem)
      )

      return {
        cases: serializableCases,
        pagination,
        statistics
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erro ao carregar casos")
    }
  }
)

export const fetchCaseById = createAsyncThunk(
  "cases/fetchCaseById",
  async (caseId, { rejectWithValue }) => {
    try {
      const response = await caseService.getById(caseId)

      // Verificar diferentes estruturas de resposta possíveis
      let caseData = null

      if (response.data) {
        // Estrutura 1: response.data.obj.case
        if (response.data.obj && response.data.obj.case) {
          caseData = response.data.obj.case
        }
        // Estrutura 2: response.data.case
        else if (response.data.case) {
          caseData = response.data.case
        }
        // Estrutura 3: response.data é o caso direto
        else {
          caseData = response.data
        }
      }

      if (!caseData) {
        return rejectWithValue("Estrutura de resposta inválida")
      }

      // Garantir que os dados são serializáveis
      caseData = ensureCaseSerializable(caseData)

      return caseData
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch case"
      )
    }
  }
)

export const createCase = createAsyncThunk(
  "cases/createCase",
  async (caseData, { rejectWithValue }) => {
    try {
      const response = await caseService.create(caseData)
      // Extrair dados do caso de diferentes estruturas possíveis
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data

      // Garantir que os dados são serializáveis
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create case"
      )
    }
  }
)

export const updateCase = createAsyncThunk(
  "cases/updateCase",
  async ({ caseId, caseData }, { rejectWithValue }) => {
    try {
      const response = await caseService.update(caseId, caseData)
      // Extrair dados do caso de diferentes estruturas possíveis
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data

      // Garantir que os dados são serializáveis
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update case"
      )
    }
  }
)

export const deleteCase = createAsyncThunk(
  "cases/deleteCase",
  async (caseId, { rejectWithValue }) => {
    try {
      await caseService.delete(caseId)
      return caseId
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete case"
      )
    }
  }
)

export const archiveCase = createAsyncThunk(
  "cases/archiveCase",
  async (caseId, { rejectWithValue }) => {
    try {
      const response = await caseService.archive(caseId)
      // Extrair dados do caso de diferentes estruturas possíveis
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to archive case"
      )
    }
  }
)

export const unarchiveCase = createAsyncThunk(
  "cases/unarchiveCase",
  async (caseId, { rejectWithValue }) => {
    try {
      const response = await caseService.unarchive(caseId)
      const caseResult = response.data?.obj

      return ensureCaseSerializable({
        ...caseResult,
        id: caseResult?.case_id || caseId
      })
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to unarchive case"
      )
    }
  }
)

export const updateCaseStatus = createAsyncThunk(
  "cases/updateCaseStatus",
  async ({ caseId, status, observation }, { rejectWithValue }) => {
    try {
      const response = await caseService.transition(caseId, { status, observation })
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update case status"
      )
    }
  }
)

export const nextCaseFlow = createAsyncThunk(
  "cases/nextCaseFlow",
  async ({ caseId, payload }, { rejectWithValue }) => {
    try {
      const response = await caseService.nextFlow(caseId, payload)
      // Extrair dados do caso de diferentes estruturas possíveis
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to advance case flow"
      )
    }
  }
)

export const updateCaseObservation = createAsyncThunk(
  "cases/updateCaseObservation",
  async ({ caseId, observation }, { rejectWithValue }) => {
    try {
      const response = await caseService.updateObservation(caseId, observation)
      // Extrair dados do caso de diferentes estruturas possíveis
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update observation"
      )
    }
  }
)

export const addCaseAttachment = createAsyncThunk(
  "cases/addCaseAttachment",
  async ({ caseId, attachment }, { rejectWithValue }) => {
    try {
      const response = await caseService.addAttachment(caseId, attachment)
      return {
        caseId,
        attachment: response.data
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add attachment"
      )
    }
  }
)

export const deleteCaseAttachment = createAsyncThunk(
  "cases/deleteCaseAttachment",
  async ({ caseId, attachmentId }, { rejectWithValue }) => {
    try {
      await caseService.deleteAttachment(caseId, attachmentId)
      return {
        caseId,
        attachmentId
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete attachment"
      )
    }
  }
)

export const fetchArchivedCases = createAsyncThunk(
  "cases/fetchArchivedCases",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await caseService.getArchivedCases(filters)

      // Verificar diferentes estruturas de resposta possíveis
      let cases = []
      let pagination = {}

      if (response.data) {
        // Estrutura 1: response.data é um array direto
        if (Array.isArray(response.data)) {
          cases = response.data
          pagination = {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.data.length
          }
        }
        // Estrutura 2: response.data.cases
        else if (response.data.cases && Array.isArray(response.data.cases)) {
          cases = response.data.cases
          pagination = response.data.pagination || {}
        }
        // Estrutura 3: response.data.obj.cases
        else if (response.data.obj && response.data.obj.cases) {
          cases = response.data.obj.cases
          pagination = response.data.obj.pagination || {}
        }
        // Estrutura 4: response.data.obj é um array
        else if (response.data.obj && Array.isArray(response.data.obj)) {
          cases = response.data.obj
          pagination = {
            currentPage: 1,
            totalPages: 1,
            totalItems: response.data.obj.length
          }
        }
      }

      // Garantir que todos os casos são serializáveis
      const serializableCases = cases.map((caseItem) =>
        ensureCaseSerializable(caseItem)
      )

      return {
        cases: serializableCases,
        pagination
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch archived cases"
      )
    }
  }
)

// Novos thunks para o fluxo de status
export const rejectValidation = createAsyncThunk(
  "cases/rejectValidation",
  async ({ caseId, message }, { rejectWithValue }) => {
    try {
      const response = await caseService.rejectValidation(caseId, { message })
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject validation"
      )
    }
  }
)

export const approveValidation = createAsyncThunk(
  "cases/approveValidation",
  async (caseId, { rejectWithValue }) => {
    try {
      const response = await caseService.approveValidation(caseId)
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve validation"
      )
    }
  }
)

export const resubmitValidation = createAsyncThunk(
  "cases/resubmitValidation",
  async (caseId, { rejectWithValue }) => {
    try {
      const response = await caseService.resubmitValidation(caseId)
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to resubmit validation"
      )
    }
  }
)

export const requestPlanningResponse = createAsyncThunk(
  "cases/requestPlanningResponse",
  async ({ caseId, message }, { rejectWithValue }) => {
    try {
      const response = await caseService.requestPlanningResponse(caseId, {
        message
      })
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to request planning response"
      )
    }
  }
)

export const respondPlanningRequest = createAsyncThunk(
  "cases/respondPlanningRequest",
  async ({ caseId, response: responseText }, { rejectWithValue }) => {
    try {
      const response = await caseService.respondPlanningRequest(caseId, {
        response: responseText
      })
      const caseResult =
        response.data?.case || response.data?.obj?.case || response.data
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to respond planning request"
      )
    }
  }
)

export const rejectServicePlanning = createAsyncThunk(
  "cases/rejectServicePlanning",
  async ({ caseId, serviceId, message }, { rejectWithValue }) => {
    try {
      const response = await caseService.rejectServicePlanning(
        caseId,
        serviceId,
        message
      )
      const caseResult = response.data?.obj?.case || response.data?.case
      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject service planning"
      )
    }
  }
)

export const approveServicePlanning = createAsyncThunk(
  "cases/approveServicePlanning",
  async ({ caseId, serviceId, password }, { rejectWithValue }) => {
    try {
      const response = await caseService.approveServicePlanning(
        caseId,
        serviceId,
        password
      )
      const caseResult = response.data?.obj?.case || response.data?.case

      if (!caseResult) {
        throw new Error(
          response.data?.message || "Case data not found in response"
        )
      }

      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to approve service planning"
      )
    }
  }
)

export const finishCase = createAsyncThunk(
  "cases/finishCase",
  async ({ caseId, finishData }, { rejectWithValue }) => {
    try {
      const response = await caseService.finishCase(caseId, finishData)

      // A resposta vem como: { error: false, message: "...", obj: { case: {...}, old_status, new_status, status_name } }
      const caseResult =
        response.data?.obj?.case || response.data?.case || response.data

      if (!caseResult) {
        throw new Error(
          response.data?.message || "Case data not found in response"
        )
      }

      return ensureCaseSerializable(caseResult)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to finish case"
      )
    }
  }
)

export const fetchStatusHistory = createAsyncThunk(
  "cases/fetchStatusHistory",
  async (caseId, { rejectWithValue }) => {
    try {
      const response = await caseService.getStatusHistory(caseId)
      return (
        response.data?.history ||
        response.data?.obj?.history ||
        response.data ||
        []
      )
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch status history"
      )
    }
  }
)

export const fetchCaseStats = createAsyncThunk(
  "cases/fetchCaseStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await caseService.getStats()
      const data = response.data?.obj || response.data || {}
      return {
        total: data.total || 0,
        byStatus: data.by_status || {}
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch case stats"
      )
    }
  }
)

export const setServicePlanner = createAsyncThunk(
  "cases/setServicePlanner",
  async ({ caseId, plannerId, serviceIds }, { rejectWithValue }) => {
    try {
      const payload = { plannerId }
      if (serviceIds && serviceIds.length > 0) payload.serviceIds = serviceIds
      const response = await caseService.setPlanner(caseId, payload)
      return response.data?.obj || response.data || {}
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to set planner"
      )
    }
  }
)

export const setServicePrinter = createAsyncThunk(
  "cases/setServicePrinter",
  async ({ caseId, printerId, serviceIds }, { rejectWithValue }) => {
    try {
      const payload = { printerId }
      if (serviceIds && serviceIds.length > 0) payload.serviceIds = serviceIds
      const response = await caseService.setPrinter(caseId, payload)
      return response.data?.obj || response.data || {}
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to set printer"
      )
    }
  }
)

export const addCaseUserByEmail = createAsyncThunk(
  "cases/addCaseUserByEmail",
  async ({ caseId, email }, { rejectWithValue }) => {
    try {
      const response = await caseService.addCaseUserByEmail(caseId, email)
      return response.data?.obj || response.data || {}
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add user"
      )
    }
  }
)

export const removeCaseUser = createAsyncThunk(
  "cases/removeCaseUser",
  async ({ caseId, userId }, { rejectWithValue }) => {
    try {
      const response = await caseService.removeCaseUser(caseId, userId)
      return { userId }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to remove user"
      )
    }
  }
)

// Cases slice
const casesSlice = createSlice({
  name: "cases",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {
        status: "all",
        search: "",
        dateRange: null,
        owner: null,
        patient: null
      }
    },
    setCurrentCase: (state, action) => {
      state.currentCase = action.payload
    },
    clearCurrentCase: (state) => {
      state.currentCase = null
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    updateCaseInState: (state, action) => {
      const updatedCase = action.payload
      state.cases[updatedCase.id] = updatedCase
      if (state.currentCase && state.currentCase.id === updatedCase.id) {
        state.currentCase = updatedCase
      }
    },
    removeCaseFromState: (state, action) => {
      const caseId = action.payload
      delete state.cases[caseId]
      if (state.currentCase && state.currentCase.id === caseId) {
        state.currentCase = null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch cases
      .addCase(fetchCases.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCases.fulfilled, (state, action) => {
        state.loading = false
        state.cases = {}
        state.caseIds = [] // Reset order array
        action.payload.cases.forEach((caseItem) => {
          state.cases[caseItem.id] = caseItem
          state.caseIds.push(caseItem.id) // Maintain server order
        })
        state.pagination = {
          currentPage: action.payload.pagination.current_page || action.payload.pagination.currentPage || 1,
          totalPages: action.payload.pagination.last_page || action.payload.pagination.totalPages || 1,
          totalItems: action.payload.pagination.total || action.payload.pagination.totalItems || 0,
          itemsPerPage: action.payload.pagination.per_page || action.payload.pagination.itemsPerPage || 15
        }
        state.statistics = action.payload.statistics
      })
      .addCase(fetchCases.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch case by ID
      .addCase(fetchCaseById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCaseById.fulfilled, (state, action) => {
        state.loading = false
        state.currentCase = action.payload
        state.cases[action.payload.id] = action.payload
      })
      .addCase(fetchCaseById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Create case
      .addCase(createCase.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createCase.fulfilled, (state, action) => {
        state.loading = false
        state.cases[action.payload.id] = action.payload
        state.currentCase = action.payload
        state.statistics.totalCases += 1
      })
      .addCase(createCase.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update case
      .addCase(updateCase.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCase.fulfilled, (state, action) => {
        state.loading = false
        state.cases[action.payload.id] = action.payload
        if (state.currentCase && state.currentCase.id === action.payload.id) {
          state.currentCase = action.payload
        }
      })
      .addCase(updateCase.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Delete case
      .addCase(deleteCase.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCase.fulfilled, (state, action) => {
        state.loading = false
        delete state.cases[action.payload]
        if (state.currentCase && state.currentCase.id === action.payload) {
          state.currentCase = null
        }
        state.statistics.totalCases -= 1
      })
      .addCase(deleteCase.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Archive case
      .addCase(archiveCase.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(archiveCase.fulfilled, (state, action) => {
        state.loading = false
        const archivedCase = action.payload
        delete state.cases[archivedCase.id]
        state.archivedCases[archivedCase.id] = archivedCase
        if (state.currentCase && state.currentCase.id === archivedCase.id) {
          state.currentCase = null
        }
      })
      .addCase(archiveCase.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Unarchive case
      .addCase(unarchiveCase.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(unarchiveCase.fulfilled, (state, action) => {
        state.loading = false
        const unarchivedCase = action.payload
        delete state.archivedCases[unarchivedCase.id]
        state.cases[unarchivedCase.id] = unarchivedCase
      })
      .addCase(unarchiveCase.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update case status
      .addCase(updateCaseStatus.fulfilled, (state, action) => {
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })

      // Update case observation
      .addCase(updateCaseObservation.fulfilled, (state, action) => {
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })

      // Next case flow
      .addCase(nextCaseFlow.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(nextCaseFlow.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(nextCaseFlow.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Add case attachment
      .addCase(addCaseAttachment.fulfilled, (state, action) => {
        const { caseId, attachment } = action.payload
        if (state.cases[caseId]) {
          state.cases[caseId].attachments.push(attachment)
        }
        if (state.currentCase && state.currentCase.id === caseId) {
          state.currentCase.attachments.push(attachment)
        }
      })

      // Delete case attachment
      .addCase(deleteCaseAttachment.fulfilled, (state, action) => {
        const { caseId, attachmentId } = action.payload
        if (state.cases[caseId]) {
          state.cases[caseId].attachments = state.cases[
            caseId
          ].attachments.filter((att) => att.id !== attachmentId)
        }
        if (state.currentCase && state.currentCase.id === caseId) {
          state.currentCase.attachments = state.currentCase.attachments.filter(
            (att) => att.id !== attachmentId
          )
        }
      })

      // Fetch archived cases
      .addCase(fetchArchivedCases.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchArchivedCases.fulfilled, (state, action) => {
        state.loading = false
        state.archivedCases = {}
        action.payload.cases.forEach((caseItem) => {
          state.archivedCases[caseItem.id] = caseItem
        })
        state.archivedPagination = {
          currentPage: action.payload.pagination.current_page || action.payload.pagination.currentPage || 1,
          totalPages: action.payload.pagination.last_page || action.payload.pagination.totalPages || 1,
          totalItems: action.payload.pagination.total || action.payload.pagination.totalItems || 0,
          itemsPerPage: action.payload.pagination.per_page || action.payload.pagination.itemsPerPage || 15
        }
      })
      .addCase(fetchArchivedCases.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Reject validation
      .addCase(rejectValidation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(rejectValidation.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(rejectValidation.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Approve validation
      .addCase(approveValidation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(approveValidation.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(approveValidation.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Resubmit validation
      .addCase(resubmitValidation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resubmitValidation.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(resubmitValidation.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Request planning response
      .addCase(requestPlanningResponse.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(requestPlanningResponse.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(requestPlanningResponse.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Respond planning request
      .addCase(respondPlanningRequest.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(respondPlanningRequest.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(respondPlanningRequest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Request approval changes
      // Reject service planning
      .addCase(rejectServicePlanning.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(rejectServicePlanning.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(rejectServicePlanning.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Approve service planning
      .addCase(approveServicePlanning.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(approveServicePlanning.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(approveServicePlanning.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Finish case
      .addCase(finishCase.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(finishCase.fulfilled, (state, action) => {
        state.loading = false
        const updatedCase = action.payload
        state.cases[updatedCase.id] = updatedCase
        if (state.currentCase && state.currentCase.id === updatedCase.id) {
          state.currentCase = updatedCase
        }
      })
      .addCase(finishCase.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch status history
      .addCase(fetchStatusHistory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchStatusHistory.fulfilled, (state, action) => {
        state.loading = false
      })
      .addCase(fetchStatusHistory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch case stats
      .addCase(fetchCaseStats.fulfilled, (state, action) => {
        state.statistics = {
          totalCases: action.payload.total,
          byStatus: action.payload.byStatus,
          byOwner: state.statistics.byOwner || {},
          avgCompletionTime: state.statistics.avgCompletionTime || 0
        }
      })

      // Set service planner
      .addCase(setServicePlanner.fulfilled, (state, action) => {
        const updatedCase = action.payload?.case
        if (updatedCase) {
          state.cases[updatedCase.id] = updatedCase
          if (state.currentCase && state.currentCase.id === updatedCase.id) {
            state.currentCase = updatedCase
          }
        }
      })

      // Set service printer
      .addCase(setServicePrinter.fulfilled, (state, action) => {
        const updatedCase = action.payload?.case
        if (updatedCase) {
          state.cases[updatedCase.id] = updatedCase
          if (state.currentCase && state.currentCase.id === updatedCase.id) {
            state.currentCase = updatedCase
          }
        }
      })

      // Add case user by email
      .addCase(addCaseUserByEmail.fulfilled, (state, action) => {
        const user = action.payload?.user
        if (user && state.currentCase) {
          if (!state.currentCase.caseUsers) state.currentCase.caseUsers = []
          state.currentCase.caseUsers.push({
            user_id: user.id,
            email: user.email,
            name: user.name,
            roles: user.roles || []
          })
        }
      })

      // Remove case user
      .addCase(removeCaseUser.fulfilled, (state, action) => {
        const { userId } = action.payload
        if (state.currentCase?.caseUsers) {
          state.currentCase.caseUsers = state.currentCase.caseUsers.filter(
            (cu) => cu.user_id !== userId
          )
        }
      })
  }
})

// Export actions
export const {
  clearError,
  setFilters,
  clearFilters,
  setCurrentCase,
  clearCurrentCase,
  setLoading,
  setPagination,
  updateCaseInState,
  removeCaseFromState
} = casesSlice.actions

// Selectors
export const selectCases = (state) => state.cases.cases
export const selectArchivedCases = (state) => state.cases.archivedCases
export const selectArchivedPagination = (state) => state.cases.archivedPagination
export const selectCurrentCase = (state) => state.cases.currentCase
export const selectCasesLoading = (state) => state.cases.loading
export const selectCasesError = (state) => state.cases.error
export const selectCasesFilters = (state) => state.cases.filters
export const selectCasesPagination = (state) => state.cases.pagination
export const selectCasesStatistics = (state) => state.cases.statistics

// Helper selectors
export const selectCaseById = (caseId) => (state) => state.cases.cases[caseId]
export const selectCasesByStatus = (status) => (state) => {
  return Object.values(state.cases.cases).filter((c) => c.status === status)
}
export const selectCasesByOwner = (ownerId) => (state) => {
  return Object.values(state.cases.cases).filter((c) => c.ownerId === ownerId)
}
export const selectCasesByPatient = (patientId) => (state) => {
  return Object.values(state.cases.cases).filter(
    (c) => c.patientId === patientId
  )
}

// Advanced selectors
export const selectFilteredCases = (state) => {
  const casesMap = state.cases.cases
  const caseIds = state.cases.caseIds || []
  const { status, search, owner, patient } = state.cases.filters

  // Mapeamento de status numéricos para strings
  const statusMap = {
    1: "new",
    2: "validation",
    3: "planning",
    4: "approval",
    5: "manufacturing",
    6: "finishing",
    7: "finished",
    99: "cancelled"
  }

  // Função para normalizar status para string
  const normalizeStatus = (s) => {
    if (typeof s === "number") {
      return statusMap[s] || "unknown"
    }
    return typeof s === "string" ? s.toLowerCase() : "unknown"
  }

  // Use caseIds to maintain server order
  return caseIds
    .map((id) => casesMap[id])
    .filter((c) => {
      if (!c) return false

      // NOTA: Filtros de status e busca são tratados pela API
      // Aqui mantemos apenas filtros locais que não são enviados à API

      // Filtro por owner (local)
      if (owner && c.owner_id !== owner) return false

      // Filtro por patient (local)
      if (patient && c.patient_id !== patient) return false

      return true
    })
}

export const selectCasesCount = (state) => Object.keys(state.cases.cases).length
export const selectArchivedCasesCount = (state) =>
  Object.keys(state.cases.archivedCases).length

export default casesSlice.reducer
